import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { analyzeSeo, slugify } from "../lib/seo";
import { prepareHtml, parseSchema } from "../lib/head";
import { Card, Field, PageTitle, Btn, Toggle, inputCls } from "./ui";
import { SeoReport } from "./SeoReport";
import { ImageField, uploadImage } from "./ImageField";
import { AiImageGenerator } from "./AiImageGenerator";
import { AiPanel } from "./AiPanel";
import { genImage, withPlaceholders, placeholders, stripPlaceholders, imgTag } from "./aiImages";
import { SchedulePanel } from "./SchedulePanel";
import { ShareCardPreview } from "./ShareCardPreview";

const EMPTY = { title: "", slug: "", excerpt: "", content_html: "", featured_image: "", featured_alt: "", tags: [], status: "draft", scheduled_at: null, show_in_footer: false, category: "", featured: false, language: "en",
  seo: { meta_title: "", meta_description: "", focus_keyword: "", canonical: "", og_image: "", schema_type: "Article", schema_json: "", noindex: false } };
const SCHEMAS = ["Article", "BlogPosting", "NewsArticle", "HowTo", "None"];

const SerpPreview = ({ title, desc, url }) => (
  <div className="rounded-xl bg-white p-4" data-testid="serp-preview">
    <p className="truncate text-xs text-[#202124]">{url}</p>
    <p className="mt-1 truncate text-lg text-[#1a0dab]">{title || "SEO title"}</p>
    <p className="mt-1 line-clamp-2 text-sm text-[#4d5156]">{desc || "Meta description will appear here."}</p>
  </div>
);

export default function PostEditor({ kind }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "new";
  const [f, setF] = useState({ ...EMPTY, kind, seo: { ...EMPTY.seo, schema_type: kind === "page" ? "None" : "Article" } });
  const [mode, setMode] = useState("plain");
  const [view, setView] = useState("write");
  const [ai, setAi] = useState({ provider: "gemini", model: "" });
  const [saving, setSaving] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [inlineBusy, setInlineBusy] = useState(0);
  const busy = saving || imgBusy || inlineBusy > 0;
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const imgRef = useRef();
  const taRef = useRef();
  const base = kind === "blog" ? "blogs" : "pages";

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => { setAi(data.settings.ai); if (isNew) setMode(data.settings.ai.mode); });
    if (!isNew) api.get(`/admin/posts/${id}`).then(({ data }) => setF(data)).catch(() => nav(`/admin/${base}`));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSeo = (k, v) => setF((p) => ({ ...p, seo: { ...p.seo, [k]: v } }));
  const result = useMemo(() => analyzeSeo({ title: f.title, metaTitle: f.seo.meta_title, metaDesc: f.seo.meta_description, slug: f.slug,
    html: f.content_html, keyword: f.seo.focus_keyword, image: f.featured_image || f.seo.og_image,
    schema: f.seo.schema_type !== "None" || parseSchema(f.seo.schema_json).length > 0 }), [f]);

  const onTitle = (v) => setF((p) => ({ ...p, title: v, slug: slugTouched ? p.slug : slugify(v) }));

  const insertImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      const alt = window.prompt("Alt text for this image", f.seo.focus_keyword || f.title) || "";
      const tag = `\n<img src="${url}" alt="${alt.replace(/"/g, "&quot;")}" />\n`;
      const pos = taRef.current?.selectionStart ?? f.content_html.length;
      set("content_html", f.content_html.slice(0, pos) + tag + f.content_html.slice(pos));
      toast.success("Image inserted");
    } catch (err) { toast.error(errMsg(err)); } finally { e.target.value = ""; }
  };

  const autoImage = async (title, excerpt) => {
    setImgBusy(true);
    try {
      const data = await genImage(title, f.seo.focus_keyword, excerpt);
      setF((p) => ({ ...p, featured_image: data.url, featured_alt: p.featured_alt || data.alt }));
      toast.success("Matching featured image added");
    } catch (e) { toast.error(`Featured image: ${errMsg(e)}`); } finally { setImgBusy(false); }
  };

  const inlineImages = async (title, html, count) => {
    const spots = placeholders(html, count);
    setInlineBusy(spots.length);
    await Promise.all(spots.map(async ({ raw, desc }) => {
      let tag = "";
      try {
        const data = await genImage(title, f.seo.focus_keyword, desc);
        tag = imgTag(data.url, desc.slice(0, 120) || data.alt);
      } catch (e) { toast.error(`Article image: ${errMsg(e)}`); }
      setF((p) => ({ ...p, content_html: p.content_html.replace(raw, tag) }));
      setInlineBusy((n) => n - 1);
    }));
    setF((p) => ({ ...p, content_html: stripPlaceholders(p.content_html) }));
    if (spots.length) toast.success("Article images added");
  };

  const onAi = (r, withImage, count = 0) => {
    const html = count ? withPlaceholders(r.html, count) : stripPlaceholders(r.html);
    setF((p) => ({ ...p, title: r.title || p.title, slug: r.slug ? slugify(r.slug) : p.slug, excerpt: r.excerpt || p.excerpt, content_html: html,
      seo: { ...p.seo, meta_title: r.metaTitle || p.seo.meta_title, meta_description: r.metaDesc || p.seo.meta_description } }));
    setView("preview");
    const title = (r.title || f.title).trim();
    if (title.length < 3) return;
    if (withImage) autoImage(title, r.excerpt);
    if (count) inlineImages(title, html, count);
  };

  const save = async (status, scheduledAt) => {
    if (!f.title.trim() || !f.slug.trim()) return toast.error("Title and slug are required");
    if (f.seo.schema_json.trim() && !parseSchema(f.seo.schema_json).length) return toast.error("Custom schema is not valid JSON");
    setSaving(true);
    try {
      const st = status || f.status;
      const body = { ...f, kind, status: st, category: f.category_auto ? "" : f.category, scheduled_at: st === "scheduled" ? (scheduledAt || f.scheduled_at) : null, seo_score: result.score };
      const { data } = isNew ? await api.post("/admin/posts", body) : await api.put(`/admin/posts/${id}`, body);
      setF(data);
      toast.success(st === "scheduled" ? `Scheduled for ${new Date(data.scheduled_at).toLocaleString()}` : "Saved");
      if (isNew) nav(`/admin/${base}/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const url = `${window.location.origin}/${kind === "blog" ? "blog" : "p"}/${f.slug || "your-slug"}`;
  return (
    <div data-testid="post-editor">
      <button onClick={() => nav(`/admin/${base}`)} className="mb-4 flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37]"><ArrowLeft size={14} /> {base}</button>
      <PageTitle title={isNew ? `New ${kind}` : `Edit ${kind}`} sub={f.status === "published" ? "Published" : f.status === "scheduled" ? `Scheduled · ${new Date(f.scheduled_at).toLocaleString()}` : "Draft"}>
        <Btn variant="outline" onClick={() => save(f.status === "scheduled" ? "scheduled" : "draft")} disabled={busy} data-testid="save-draft">{f.status === "scheduled" ? "Save" : "Save draft"}</Btn>
        <Btn onClick={() => save("published")} disabled={busy} data-testid="publish">{saving ? "Saving…" : imgBusy || inlineBusy ? "Creating image…" : f.status === "scheduled" ? "Publish now" : "Publish"}</Btn>
      </PageTitle>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px] [&>*]:min-w-0">
        <div className="min-w-0 space-y-6">
          <Card>
            <div className="space-y-4">
              <Field label="Title"><input value={f.title} onChange={(e) => onTitle(e.target.value)} className={`${inputCls} text-lg`} data-testid="post-title-input" /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Slug" hint={<span className="break-all">{url}</span>}><input value={f.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} className={inputCls} data-testid="post-slug-input" /></Field>
                <Field label="Language">
                  <select value={f.language} onChange={(e) => set("language", e.target.value)} className={inputCls} data-testid="post-language">
                    <option value="en">English</option><option value="hi">Hindi</option>
                  </select>
                </Field>
              </div>
              <Field label="Excerpt"><textarea rows={2} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} className={inputCls} data-testid="post-excerpt" /></Field>
            </div>
          </Card>
          <div className="flex rounded-full border border-[#D4AF37]/30 p-0.5 sm:w-fit">
            {[["plain", "Plain HTML"], ["ai", "AI generated"]].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`mode-${k}`} className={`flex-1 whitespace-nowrap rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-wider ${mode === k ? "bg-[#D4AF37] text-black" : "text-[#D4AF37]"}`}>{l}</button>
            ))}
          </div>
          {mode === "ai" && <AiPanel keyword={f.seo.focus_keyword} language={f.language} onResult={onAi} provider={`${ai.provider} · ${ai.model}`} />}
          <Card title="Content (HTML)">
            {inlineBusy > 0 && (
              <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#F3D77A]" data-testid="inline-images-busy">
                <Loader2 size={14} className="animate-spin" /> Creating {inlineBusy} gold image{inlineBusy > 1 ? "s" : ""} for the article…
              </div>
            )}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {["write", "preview"].map((v) => (
                <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-wider ${view === v ? "bg-white/10 text-white" : "text-zinc-500"}`}>{v}</button>
              ))}
              <button onClick={() => imgRef.current.click()} data-testid="insert-image" className="ml-auto flex items-center gap-2 text-xs uppercase tracking-wider text-[#D4AF37]"><ImagePlus size={14} /> Insert image</button>
              <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={insertImage} />
            </div>
            {view === "write" ? (
              <textarea ref={taRef} rows={22} value={f.content_html} onChange={(e) => set("content_html", e.target.value)} spellCheck={false}
                className={`${inputCls} font-mono text-xs leading-relaxed`} placeholder="<h2>Heading</h2>\n<p>Your paragraph…</p>" data-testid="post-html-input" />
            ) : (
              <div className="prose-gold max-h-[640px] overflow-auto rounded-lg bg-black/30 p-5" data-testid="post-html-preview" dangerouslySetInnerHTML={{ __html: prepareHtml(f.content_html) }} />
            )}
          </Card>
        </div>
        <div className="space-y-6">
          <SeoReport result={result} />
          <Card title="SEO settings" testid="seo-settings">
            <div className="space-y-4">
              <Field label="Focus keyword"><input value={f.seo.focus_keyword} onChange={(e) => setSeo("focus_keyword", e.target.value)} className={inputCls} data-testid="seo-keyword" /></Field>
              <Field label={`SEO title (${(f.seo.meta_title || f.title).length}/60)`}><input value={f.seo.meta_title} placeholder={f.title} onChange={(e) => setSeo("meta_title", e.target.value)} className={inputCls} data-testid="seo-meta-title" /></Field>
              <Field label={`Meta description (${f.seo.meta_description.length}/160)`}><textarea rows={3} value={f.seo.meta_description} onChange={(e) => setSeo("meta_description", e.target.value)} className={inputCls} data-testid="seo-meta-desc" /></Field>
              <SerpPreview title={f.seo.meta_title || f.title} desc={f.seo.meta_description} url={url} />
              <Field label="Canonical URL (optional)"><input value={f.seo.canonical} onChange={(e) => setSeo("canonical", e.target.value)} className={inputCls} data-testid="seo-canonical" /></Field>
              <Field label="Schema type">
                <select value={f.seo.schema_type} onChange={(e) => setSeo("schema_type", e.target.value)} className={inputCls} data-testid="seo-schema-type">
                  {SCHEMAS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Custom JSON-LD schema (optional)" hint="FAQPage, Product, Review, etc. Must be valid JSON.">
                <textarea rows={5} value={f.seo.schema_json} onChange={(e) => setSeo("schema_json", e.target.value)} spellCheck={false} className={`${inputCls} font-mono text-xs`} placeholder='{"@context":"https://schema.org","@type":"FAQPage",...}' data-testid="seo-schema-json" />
              </Field>
              <Toggle checked={f.seo.noindex} onChange={(v) => setSeo("noindex", v)} testid="seo-noindex" label="Hide from search engines (noindex)" />
            </div>
          </Card>
          {f.status !== "published" && <SchedulePanel status={f.status} scheduledAt={f.scheduled_at} disabled={busy}
            onSchedule={(iso) => save("scheduled", iso)} onCancel={() => save("draft")} />}
          <Card title="Featured image">
            {imgBusy && (
              <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#F3D77A]" data-testid="auto-image-busy">
                <Loader2 size={14} className="animate-spin" /> Creating a matching gold featured image…
              </div>
            )}
            <ImageField value={f.featured_image} onChange={(v) => set("featured_image", v)} />
            <AiImageGenerator title={f.title} keyword={f.seo.focus_keyword} hasImage={!!f.featured_image}
              onDone={({ url, alt }) => { set("featured_image", url); if (!f.featured_alt) set("featured_alt", alt); }} />
            <div className="mt-3"><Field label="Alt text"><input value={f.featured_alt} onChange={(e) => set("featured_alt", e.target.value)} className={inputCls} data-testid="featured-alt" /></Field></div>
          </Card>
          {!isNew && kind === "blog" && <ShareCardPreview key={`${f.updated_at}`} postId={id} slug={f.slug} title={f.title} image={f.featured_image} published={f.status === "published"} />}
          <Card title="Options">
            <div className="space-y-4">
              <Field label="Tags (comma separated)"><input value={f.tags.join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className={inputCls} data-testid="post-tags" /></Field>
              {kind === "blog" && (
                <>
                  <Field label="Category" hint={f.category_auto && f.category ? `Auto-detected: ${f.category}. Re-checked from the title and keyword on every save.` : "Auto picks Cricket or Casino from the title and keyword when you save."}>
                    <select value={f.category_auto ? "" : f.category || ""} onChange={(e) => setF((p) => ({ ...p, category: e.target.value, category_auto: !e.target.value }))} className={inputCls} data-testid="post-category">
                      <option value="">Auto-detect</option><option value="cricket">Cricket</option><option value="casino">Casino</option><option value="general">General</option>
                    </select>
                  </Field>
                  <Toggle checked={!!f.featured} onChange={(v) => set("featured", v)} testid="post-featured" label="Feature as the big gold card on the blog page" />
                  <p className="-mt-2 text-xs text-zinc-500">Only one post is featured at a time. If none is chosen, the newest post is featured.</p>
                </>
              )}
              {kind === "page" && <Toggle checked={f.show_in_footer} onChange={(v) => set("show_in_footer", v)} testid="show-in-footer" label="Show link in site footer" />}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
